import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  // モダンなカラーパレット
  palette: {
    mode: 'light',
    primary: {
      main: '#2E3440', // ダークグレー（モダンなネイビー代替）
      light: '#4C566A',
      dark: '#1B202B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#5E81AC', // ソフトブルー
      light: '#88C0D0',
      dark: '#4C566A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF', // 純白背景
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2E3440', // ダークグレーテキスト
      secondary: '#5E81AC',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    divider: '#E5E7EB', // 薄いグレーの区切り線
  },
  
  // レスポンシブなタイポグラフィ設定
  typography: {
    // 基本フォントサイズをモバイルで小さく
    fontSize: 14,
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    
    // ヘッダー要素をレスポンシブに
    h1: {
      fontSize: '1.5rem', // デフォルトより小さく
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontSize: '1.3rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:960px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '1.3rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '1.2rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.3rem',
      },
    },
    h5: {
      fontSize: '0.9rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '1.1rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.2rem',
      },
    },
    h6: {
      fontSize: '0.85rem',
      fontWeight: 600,
      '@media (min-width:600px)': {
        fontSize: '1rem',
      },
      '@media (min-width:960px)': {
        fontSize: '1.1rem',
      },
    },
    
    // 本文テキスト
    body1: {
      fontSize: '0.875rem', // 14px - モバイルで読みやすい
      lineHeight: 1.5,
      '@media (min-width:600px)': {
        fontSize: '1rem', // 16px - タブレット以上
      },
    },
    body2: {
      fontSize: '0.8rem', // 12.8px - モバイル
      lineHeight: 1.4,
      '@media (min-width:600px)': {
        fontSize: '0.875rem', // 14px - タブレット以上
      },
    },
    
    // ボタンテキスト
    button: {
      fontSize: '0.8rem', // モバイルでコンパクト
      fontWeight: 500,
      textTransform: 'none', // 大文字変換なし
      '@media (min-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    
    // キャプション
    caption: {
      fontSize: '0.7rem',
      '@media (min-width:600px)': {
        fontSize: '0.75rem',
      },
    },
  },
  
  // コンポーネントのデフォルト設定をオーバーライド
  components: {
    // AppBarを白基調のモダンデザインに
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // 白背景
          color: '#2E3440', // ダークグレーテキスト
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // 薄いシャドウ
          borderBottom: '1px solid #E5E7EB', // 薄いボーダー
          minHeight: '56px', // モバイルでコンパクト
          '@media (min-width:600px)': {
            minHeight: '64px',
          },
        },
      },
    },
    
    // Toolbarの高さ調整
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          paddingLeft: '12px !important',
          paddingRight: '12px !important',
          '@media (min-width:600px)': {
            minHeight: '64px !important',
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
        },
      },
    },
    
    // ボタンを近未来的なデザインに調整
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // ピル型
          textTransform: 'none', // 大文字変換なし
          fontWeight: 500,
          minHeight: '36px',
          padding: '8px 20px',
          position: 'relative',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
            transition: 'left 0.5s ease',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            '&::before': {
              left: '100%',
            }
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '@media (min-width:600px)': {
            minHeight: '42px',
            padding: '10px 24px',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          color: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            background: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          },
        },
        outlined: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: '#1E293B',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderColor: 'rgba(59, 130, 246, 0.4)',
          },
        },
        text: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          color: '#1E293B',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    
    // Containerの余白調整
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '12px !important',
          paddingRight: '12px !important',
          '@media (min-width:600px)': {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
        },
      },
    },
    
    // Paperを近未来的なデザインに調整
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '20px', // より大きな角丸
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent)',
          },
          '&.MuiCard-root': {
            padding: '12px',
            '@media (min-width:600px)': {
              padding: '16px',
            },
            '@media (min-width:960px)': {
              padding: '20px',
            },
          },
        },
      },
    },
    
    // Cardの近未来的なスタイル
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            right: '-100px',
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transition: 'right 0.3s ease',
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            '&::after': {
              right: '-50px',
            }
          },
        },
      },
    },
    
    // TextFieldを近未来的なデザインに
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(59, 130, 246, 0.4)',
              boxShadow: '0 0 10px rgba(59, 130, 246, 0.2)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              '& fieldset': {
                borderColor: 'rgba(59, 130, 246, 0.6)',
                borderWidth: '2px',
                boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)',
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: '#64748B',
            '&.Mui-focused': {
              color: '#3B82F6',
            },
          },
        },
      },
    },
    
    // Chipを近未来的なデザインに
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: '#1E293B',
          fontWeight: 500,
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
          '&.MuiChip-colorPrimary': {
            background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
            color: '#FFFFFF',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%)',
            },
          },
        },
      },
    },
    
    // Autocompleteを近未来的に
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
        },
        option: {
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
          },
        },
      },
    },
  },
  
  // スペーシングをモバイル向けに調整
  spacing: (factor: number) => `${0.5 * factor}rem`,
  
  // ブレークポイント
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
